import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { Menu } from "antd";
import type { MenuProps } from "antd";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { RightOutlined } from "@ant-design/icons";
import type { UserInfo } from "../../types";
import type { Menu as AppMenu } from "../../service/menu/types";
import { globalStore } from "../../utils/globalStore";
import { convertHomeShellMenusToAntdItems } from "../../utils/homeShellMenuAntd";
import {
  buildSidebarSections,
  resolveHomeShellMenuSelection,
} from "../../utils/homeShellSidebar";
import { partitionMenuTreeByPosition } from "../../utils/homeShellMenuByPosition";
import {
  MARKETING_PAGE_PATH,
  resolveMenuNavigationPath,
} from "../../utils/menuPathNormalize";
import { getRoleDisplayLabel } from "../../utils/roleDisplayName";
import { getDicebearAvataaarsUrl } from "./dicebearAvatar";
import { AlertMessage } from "../../components/common/AlertMessage";
import { ApiKeyModal } from "../../components/common/ApiKeyModal";
import { ProfileModal } from "../../components/common/ProfileModal";
import { MemberModal } from "../../components/common/MemberModal";
import {
  ShellActionsProvider,
  useShellActions,
} from "../../components/common/ShellActionsContext";
import {
  ShellCreditsProvider,
  useShellCredits,
} from "../../components/common/ShellCreditsContext";
import {
  ShellNotificationsProvider,
  useShellNotifications,
} from "../../components/common/ShellNotificationsContext";
import { SidebarTools } from "./SidebarTools";
import { AccountMenuPopup } from "./AccountMenuPopup";
import { ShellModals } from "./ShellModals";
import { useAlert } from "../../hooks/useAlert";
import { useHasPermission } from "../../hooks/useHasPermission";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { ALERT_Z_INDEX } from "../../utils/alertManager";
import { InitService } from "../../utils/initService";
import { UserRoleManager } from "../../utils/userRole";
import { logoutApi } from "../../service/user";
import {
  getMembershipCurrentApi,
  triggerMembershipRefresh,
} from "../../service/membership";
import { useOperatorTeamModal } from "../../hooks/useOperatorTeamModal";
import "./homeShell.scss";

const USER_GUIDE_FEISHU_URL =
  "https://mcnwgnsxxswu.feishu.cn/wiki/Xnh0wbajKipgUkkDHGDcQnihnte";

function openUserGuideInNewTab() {
  window.open(USER_GUIDE_FEISHU_URL, "_blank", "noopener,noreferrer");
}

/**
 * 侧栏底部入口区适配器：读取 Shell 级上下文（积分 / 通知 / 弹窗动作）与功能权限，
 * 再以纯 props 注入 SidebarTools，保证 SidebarTools 保持展示组件定位。
 */
const SidebarFooterTools: FC = () => {
  const credits = useShellCredits();
  const notifications = useShellNotifications();
  const actions = useShellActions();
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW);
  const hasGuide = useHasPermission(PERMISSIONS.GUIDE_VIEW);
  const hasNotify = useHasPermission(PERMISSIONS.NOTIFICATION_VIEW);

  if (!credits || !notifications || !actions) return null;

  const showCredits = hasPoints === true;
  const showGuide = hasGuide === true;
  const showNotification = hasNotify === true;

  if (!showCredits && !showGuide && !showNotification) return null;

  return (
    <SidebarTools
      credits={{
        text: credits.hidden ? "0" : credits.creditsText,
        onClick: actions.openPointsDetail,
        onBuy: actions.openPurchase,
        canBuy: !credits.hidden,
      }}
      showCredits={showCredits}
      notification={{
        unreadCount: notifications.unreadCount,
        onClick: notifications.openInbox,
      }}
      showNotification={showNotification}
      onOpenGuide={openUserGuideInNewTab}
      showGuide={showGuide}
    />
  );
};

export interface HomeShellProps {
  children: ReactNode;
}

/**
 * 文档稿中的「壳」：左侧栏 + 右侧主栏占位。
 * 侧边栏菜单由后端菜单树驱动，不再依赖标题字符串匹配或硬编码 ID。
 */
export const HomeShell: FC<HomeShellProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  /** 当前打开的 group key（null 表示二级 popup 关闭） */
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const footerRef = useRef<HTMLDivElement | null>(null);
  const userTriggerRef = useRef<HTMLDivElement | null>(null);
  const [userInfo, setUserInfo] = useState<unknown>(() =>
    globalStore.getUserInfo(),
  );
  const [menuList, setMenuList] = useState(() => globalStore.getMenuList());
  const [sidebarOpenKeys, setSidebarOpenKeys] = useState<string[]>([]);
  const [pendingSelect, setPendingSelect] = useState<string | null>(null);

  useLayoutEffect(() => {
    const next = globalStore.getUserInfo();
    if (next) {
      UserRoleManager.updateUserRoleInfo(next as UserInfo);
    }
    setUserInfo(next);
    setMenuList(globalStore.getMenuList());
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserInfo = globalStore.getUserInfo();
    const permissions = storedUserInfo?.permissions || [];
    const needInit =
      !!token &&
      (!InitService.isInitialized() ||
        !storedUserInfo ||
        permissions.length === 0);

    if (!needInit) return;

    let cancelled = false;
    InitService.initializeUserData()
      .then(() => {
        if (cancelled) return;
        const next = globalStore.getUserInfo();
        if (next) {
          UserRoleManager.updateUserRoleInfo(next as UserInfo);
        }
        setUserInfo(next);
        setMenuList(globalStore.getMenuList());
      })
      .catch((error) => {
        console.error("HomeShell: 初始化用户数据失败:", error);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return globalStore.subscribe(() => {
      const next = globalStore.getUserInfo();
      if (next) {
        UserRoleManager.updateUserRoleInfo(next as UserInfo);
      }
      setUserInfo(next);
      setMenuList(globalStore.getMenuList());
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("open_api_key") === "1") {
      setApiKeyModalOpen(true);
      params.delete("open_api_key");
      const cleanSearch = params.toString();
      const cleanUrl =
        window.location.pathname +
        (cleanSearch ? `?${cleanSearch}` : "") +
        window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [location]);

  /**
   * 全局监听 membership:refresh 事件（业务侧在支付/购买/积分消耗成功后派发）：
   * 重新拉取 /video/membership/current 并写回 globalStore，
   * AccountMenuPopup / MemberModal 通过订阅 globalStore 自动同步最新数据。
   */
  useEffect(() => {
    const onRefresh = () => {
      void getMembershipCurrentApi()
        .then((res) => {
          if (res?.code === 0 && res.data) {
            globalStore.setMembership(res.data);
          }
        })
        .catch((err) => {
          console.warn("HomeShell: 重新拉取会员快照失败", err);
        });
    };
    window.addEventListener("membership:refresh", onRefresh);
    return () => window.removeEventListener("membership:refresh", onRefresh);
  }, []);

  /**
   * 触发 1：路由变化时刷新会员快照
   * 任意页面切换都触发一次；事件本身在 service 层有 1.5s 节流，避免短路径内重复打点。
   */
  useEffect(() => {
    triggerMembershipRefresh();
  }, [location.pathname]);

  /**
   * 触发 2：浏览器 Tab 切回前台时刷新会员快照（全局只注册一次）
   * 用户最小化 / 切到别的 App 再回来时立即拉一次；后台时不会触发。
   */
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerMembershipRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  const { sidebarTree, avatarPopupGroups, looseAvatarPopupItems } = useMemo(
    () => partitionMenuTreeByPosition(menuList),
    [menuList],
  );

  const sections = useMemo(
    () => buildSidebarSections(sidebarTree),
    [sidebarTree],
  );

  const sectionsWithAntdItems = useMemo(
    () =>
      sections.map((sec) => ({
        ...sec,
        key: `sec-${sec.root.id ?? sec.label}`,
        antdItems: convertHomeShellMenusToAntdItems(sec.children),
      })),
    [sections],
  );

  const allMenuChildren = useMemo(
    () => sections.flatMap((sec) => sec.children),
    [sections],
  );

  const selectedKeys = useMemo(() => {
    if (pendingSelect) return [pendingSelect];
    if (!allMenuChildren.length) return [];
    const hit = resolveHomeShellMenuSelection(
      location.pathname,
      allMenuChildren,
    );
    return hit?.selected ? [hit.selected] : [];
  }, [location.pathname, allMenuChildren, pendingSelect]);

  useEffect(() => {
    if (!allMenuChildren.length) return;
    const hit = resolveHomeShellMenuSelection(
      location.pathname,
      allMenuChildren,
    );
    setSidebarOpenKeys(hit?.openKeys ?? []);
  }, [location.pathname, allMenuChildren]);

  useLayoutEffect(() => {
    if (!pendingSelect || !allMenuChildren.length) return;
    const hit = resolveHomeShellMenuSelection(
      location.pathname,
      allMenuChildren,
    );
    if (hit?.selected === pendingSelect) {
      setPendingSelect(null);
      return;
    }
    if (!hit?.selected) setPendingSelect(null);
  }, [location.pathname, allMenuChildren, pendingSelect]);

  const handleSidebarMenuClick: MenuProps["onClick"] = (info) => {
    const { key } = info;
    if (key.startsWith("dir-")) return;
    if (/^https?:\/\//i.test(key)) {
      window.open(key, "_blank", "noopener,noreferrer");
      return;
    }
    flushSync(() => {
      setPendingSelect(key);
    });
    navigate(key);
  };

  const { alertConfig, hideAlert, showInfo, showSuccess, showError } =
    useAlert();
  const { teamAction, teamModal } = useOperatorTeamModal({
    userInfo,
    showSuccess,
    showError,
  });

  const displayName = (() => {
    const u = (userInfo || {}) as {
      nickName?: string;
      nickname?: string;
      nick_name?: string;
      realName?: string;
      name?: string;
      username?: string;
    };
    return (
      u.nickName?.trim() ||
      u.nickname?.trim() ||
      u.nick_name?.trim() ||
      u.realName?.trim() ||
      u.name?.trim() ||
      u.username?.trim() ||
      ""
    );
  })();
  const avatarSrc = getDicebearAvataaarsUrl(userInfo);
  const roleDisplayLabel = useMemo(
    () => getRoleDisplayLabel(userInfo),
    [userInfo],
  );

  const handleLogout = () => {
    setMenuOpen(false);
    setActiveGroupKey(null);
    void logoutApi().catch(() => {});
    localStorage.removeItem("token");
    InitService.clearUserData();
    window.location.replace("/login");
  };

  const handleAvatarPopupNavigate = (menu: AppMenu.Menu) => {
    const p = resolveMenuNavigationPath(
      (menu.path || "").trim(),
      menu.component,
    );
    if (!p) return;
    if (/^https?:\/\//i.test(p)) {
      window.open(p, "_blank", "noopener,noreferrer");
      return;
    }
    flushSync(() => {
      setPendingSelect(p);
    });
    navigate(p);
  };

  /**
   * click-only popup 状态管理：
   * - avatar click  → toggleMenu()：menuOpen 切换
   * - group toggle click → onToggleGroup()：activeGroupKey 切换（独立于 menuOpen）
   * - 叶子 click → 路由 / 业务 + closeAll()
   * - outside click → closeAll()
   */
  const closeAll = useCallback(() => {
    setMenuOpen(false);
    setActiveGroupKey(null);
  }, []);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      if (prev) {
        setActiveGroupKey(null);
        return false;
      }
      return true;
    });
  }, []);

  const handleAvatarItemClick = useCallback(
    (menu: AppMenu.Menu) => {
      closeAll();

      // 按菜单 meta.action 触发对应 modal：
      //   action 值 = 弹窗组件名（PascalCase），见 src/service/menu/types.d.ts 中 action 字段说明
      switch (menu.meta?.action) {
        case "ProfileModal":
          setProfileModalOpen(true);
          return;
        case "ApiKeyModal":
          setApiKeyModalOpen(true);
          return;
        case "MemberModal":
          setMemberModalOpen(true);
          return;
        default:
          break;
      }

      const p = resolveMenuNavigationPath(
        (menu.path || "").trim(),
        menu.component,
      );
      if (!p) {
        return;
      }
      if (/^https?:\/\//i.test(p)) {
        window.open(p, "_blank", "noopener,noreferrer");
        return;
      }
      flushSync(() => {
        setPendingSelect(p);
      });
      navigate(p);
    },
    [closeAll, navigate],
  );

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const root = footerRef.current;
      if (!root || !(e.target instanceof Node)) return;
      if (!root.contains(e.target)) {
        closeAll();
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [closeAll]);

  return (
    <div className="home-shell">
      <AlertMessage
        config={alertConfig}
        onClose={hideAlert}
        zIndex={ALERT_Z_INDEX.NAVBAR}
      />
      {teamModal}
      <ApiKeyModal
        open={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
      />
      <ProfileModal
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        userInfo={userInfo}
        roleLabel={roleDisplayLabel}
      />
      <ShellNotificationsProvider
        showSuccess={showSuccess}
        showError={showError}
      >
        <ShellCreditsProvider>
          <ShellActionsProvider>
            <ShellModals />
            <MemberModal
              open={memberModalOpen}
              onClose={() => setMemberModalOpen(false)}
            />
            <aside className="sidebar">
              <div className="sidebar-decor-top" />
              <div className="sidebar-decor-bottom" />

              <NavLink to={MARKETING_PAGE_PATH} className="sidebar-logo">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <div className="sidebar-logo-mark" />
                  <div>
                    <h1 className="sidebar-logo-text">Lychee Hub</h1>
                    <p className="sidebar-logo-sub">AI创作平台</p>
                  </div>
                </div>
              </NavLink>

              <nav className="sidebar-nav" aria-label="主导航">
                {sectionsWithAntdItems.map((sec, idx) =>
                  sec.antdItems && sec.antdItems.length > 0 ? (
                    <React.Fragment key={sec.key}>
                      <div
                        className={`sidebar-section-label${
                          idx > 0 ? " sidebar-section-label--spaced" : ""
                        }`}
                      >
                        {sec.label}
                      </div>
                      <div className="sidebar-nav-menu-wrap">
                        <Menu
                          mode="inline"
                          className="home-shell-sidebar-menu"
                          items={sec.antdItems}
                          selectedKeys={selectedKeys}
                          openKeys={sidebarOpenKeys}
                          onOpenChange={setSidebarOpenKeys}
                          onClick={handleSidebarMenuClick}
                        />
                      </div>
                    </React.Fragment>
                  ) : null,
                )}
              </nav>

              {userInfo ? (
                <div className="sidebar-footer" ref={footerRef}>
                  <SidebarFooterTools />
                  <div className="sidebar-user-wrap">
                    <div
                      ref={userTriggerRef}
                      className="sidebar-user"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMenu();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleMenu();
                        }
                      }}
                    >
                      <div className="sidebar-avatar">
                        <img
                          className="sidebar-avatar-img"
                          src={avatarSrc}
                          alt={
                            displayName ? `${displayName} 的头像` : "用户头像"
                          }
                          width={36}
                          height={36}
                          decoding="async"
                          loading="lazy"
                        />
                      </div>
                      <p className="sidebar-username">{displayName}</p>
                      <div className="sidebar-user-trailing">
                        <span className="sidebar-user-role">
                          {roleDisplayLabel}
                        </span>
                        <RightOutlined className="sidebar-user-chevron" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </aside>

            <div className="home-shell__main">{children}</div>

            <AccountMenuPopup
              open={menuOpen}
              anchorRef={userTriggerRef}
              onClose={closeAll}
              avatarPopupGroups={avatarPopupGroups}
              looseAvatarPopupItems={looseAvatarPopupItems}
              activeGroupKey={activeGroupKey}
              onToggleGroup={setActiveGroupKey}
              onItemClick={handleAvatarItemClick}
              onLogout={handleLogout}
            />
          </ShellActionsProvider>
        </ShellCreditsProvider>
      </ShellNotificationsProvider>
    </div>
  );
};
